import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Sparkles, Send, Square, X, RotateCcw, Plus, Check, Wand2, Settings, FileStack } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AiAttachments } from './AiAttachments';
import { AiChat } from './AiChat';
import { PanelErrorBoundary } from './PanelErrorBoundary';
import { toast } from 'sonner';
import { AiSources } from './AiSources';
import { AiSettingsDialog } from './AiSettingsDialog';

const ACTIONS = { draft: 'Draft a page', rewrite: 'Improve writing', layout: 'Design page structure', summary: 'Summarise', continue: 'Continue writing' };
const clean = html => DOMPurify.sanitize(html.replace(/^\s*```(?:html)?\s*|\s*```\s*$/gi, ''), {
  FORBID_TAGS: ['style', 'script', 'iframe', 'img', 'video', 'form', 'input', 'button'], FORBID_ATTR: ['style']
});

export const PageAI = ({ form, setForm, uploadFile, onApply, onClose, onUndo, canUndo, isAdmin }) => {
  const [action, setAction] = useState('draft');
  const [instruction, setInstruction] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [links, setLinks] = useState([]);
  const [useKb, setUseKb] = useState(true);
  const [structures, setStructures] = useState([]);
  const [structure, setStructure] = useState('');
  const [sources, setSources] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tab, setTab] = useState('write');
  const [suggestion, setSuggestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [turns, setTurns] = useState([]);
  const [selectedTurn, setSelectedTurn] = useState('latest');
  const controller = useRef(null);
  useEffect(() => () => controller.current?.abort(), []);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/kb/ai/structures`,
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(res => setStructures(res.data.structures || []))
      .catch(() => setStructures([]));
  }, []);

  const generate = async () => {
    if (busy || instruction.trim().length < 3) return;
    if (attachments.some(item => item.status === 'processing')) { setError('Wait until every attached document has been read.'); return; }
    controller.current = new AbortController();
    setBusy(true); setComplete(false); setError(''); setSuggestion(''); setSelectedTurn('latest'); setSources([]);
    let html = '';
    let done = false;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kb/ai/draft`, {
        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, instruction: instruction.trim(), session_id: sessionId,
          title: form.title, body: form.body, template: form.template,
          document_ids: attachments.filter(item => item.kind === 'pdf' && item.status === 'ready').map(item => item.docId),
          image_file_ids: attachments.filter(item => item.kind === 'image').map(item => item.fileId),
          link_ids: links.map(item => item.id),
          use_knowledge_base: useKb,
          structure: structure || null,
          page_id: form.id || null }), signal: controller.current.signal
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not generate a suggestion. Check the page length and try again.');
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const chunk = await reader.read();
        if (chunk.done) break;
        buffer += decoder.decode(chunk.value, { stream: true });
        const events = buffer.split('\n\n'); buffer = events.pop();
        for (const item of events) {
          if (!item.startsWith('data: ')) continue;
          const event = JSON.parse(item.slice(6));
          if (event.type === 'session') setSessionId(event.session_id);
          if (event.type === 'delta') { html += event.text; setSuggestion(clean(html)); }
          if (event.type === 'error') throw new Error(event.message);
          if (event.type === 'done') {
            done = true; setSuggestion(clean(event.html)); setComplete(true); setSources(event.sources || []);
            setTurns(prev => [...prev, { instruction: instruction.trim(), html: clean(event.html), id: event.turn_id }]);
          }
        }
      }
      if (!done) throw new Error('The response was interrupted. Your page is unchanged. Please try again.');
    } catch (err) {
      setComplete(false);
      setError(err.name === 'AbortError' ? 'Generation stopped. Your page is unchanged.' : err.message);
    } finally { setBusy(false); }
  };

  return <aside className="kb-ai-panel flex h-full min-h-0 flex-col border-l border-slate-200 bg-white" data-testid="kb-ai-panel">
    <header className="flex shrink-0 items-center justify-between gap-2 border-b p-4">
      <h2 className="flex items-center gap-2 font-semibold text-slate-800"><Sparkles className="h-4 w-4 text-indigo-600" />Officium AI</h2>
      <div className="flex items-center gap-1">
        {isAdmin && <Button data-testid="kb-ai-settings-open" variant="ghost" size="icon" title="Officium AI settings" onClick={() => setSettingsOpen(true)}><Settings className="h-4 w-4" /></Button>}
        <Button data-testid="kb-ai-close" variant="ghost" size="icon" title="Close AI panel" onClick={onClose}><X className="h-4 w-4" /></Button>
      </div>
    </header>
    <AiSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    <div className="flex shrink-0 gap-1 border-b border-slate-100 px-4 pt-2">
      {[['write', 'Write a page'], ['chat', 'Chat & talk']].map(([id, label]) => (
        <button key={id} type="button" onClick={() => setTab(id)} data-testid={`kb-ai-tab-${id}`}
          className={`rounded-t-lg px-3 py-1.5 text-xs font-medium transition-colors ${tab === id ? 'bg-indigo-50 text-indigo-700' : 'text-slate-500 hover:text-slate-700'}`}>
          {label}
        </button>
      ))}
    </div>
    {tab === 'chat' ? (
      <div className="min-h-0 flex-1 p-4">
        <PanelErrorBoundary>
        <AiChat sessionId={sessionId || `chat-${form.id || 'new'}`} onInsert={(text, options) => {
          const html = options?.html ? text
            : text.split(/\n{2,}/).map(block => `<p>${block.replace(/\n/g, '<br />')}</p>`).join('');
          setForm(prev => ({ ...prev, body: `${prev.body || ''}${html}` }));
          toast.success('Added to the page');
        }} />
        </PanelErrorBoundary>
      </div>
    ) : (<>
    <div className="min-h-0 flex-1 overflow-y-auto p-4 space-y-4">
      <div className="space-y-2"><Label htmlFor="kb-ai-action">Writing task</Label>
        <Select value={action} onValueChange={setAction} disabled={busy}><SelectTrigger id="kb-ai-action" data-testid="kb-ai-action"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(ACTIONS).map(([value, label]) => <SelectItem key={value} value={value} data-testid={`kb-ai-action-${value}`}>{label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {structures.length > 0 && (
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5"><FileStack className="h-3.5 w-3.5" />Ready-made structure</Label>
          <div className="flex flex-wrap gap-1.5">
            <button type="button" onClick={() => setStructure('')} disabled={busy} data-testid="kb-ai-structure-none"
              className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${structure === '' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
              Free form
            </button>
            {structures.map(item => (
              <button key={item.id} type="button" onClick={() => setStructure(item.id)} disabled={busy}
                title={item.sections.join(' · ')} data-testid={`kb-ai-structure-${item.id}`}
                className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${structure === item.id ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="space-y-2"><Label htmlFor="kb-ai-prompt">Your instructions</Label>
        <Textarea id="kb-ai-prompt" data-testid="kb-ai-prompt" value={instruction} onChange={e => setInstruction(e.target.value)} maxLength={4000} rows={4}
          placeholder="Draft a telephonic screening SOP with objectives, steps, a checklist and escalation guidance." disabled={busy} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="kb-ai-generate" onClick={generate} disabled={busy || instruction.trim().length < 3} className="bg-indigo-600 hover:bg-indigo-700">
          {busy ? <Wand2 className="mr-2 h-4 w-4 animate-pulse" /> : <Send className="mr-2 h-4 w-4" />}{busy ? 'Writing…' : turns.length ? 'Refine suggestion' : 'Generate suggestion'}
        </Button>
        {busy && <Button data-testid="kb-ai-stop" variant="outline" onClick={() => controller.current?.abort()}><Square className="mr-2 h-3 w-3" />Stop</Button>}
      </div>
      <AiSources links={links} setLinks={setLinks} useKb={useKb} setUseKb={setUseKb} disabled={busy} />
      <AiAttachments items={attachments} setItems={setAttachments} uploadFile={uploadFile} setForm={setForm} disabled={busy} />
      {error && <p data-testid="kb-ai-error" role="alert" className="text-sm text-red-700 break-words">{error}</p>}
      {turns.length > 1 && <div className="space-y-2"><Label>Earlier suggestions</Label><Select value={selectedTurn} onValueChange={id => {
        setSelectedTurn(id);
        const turn = id === 'latest' ? turns[turns.length - 1] : turns.find(t => t.id === id);
        if (turn) { setSuggestion(turn.html); setComplete(true); }
      }} disabled={busy}><SelectTrigger data-testid="kb-ai-history-select"><SelectValue /></SelectTrigger><SelectContent>
        <SelectItem data-testid="kb-ai-history-latest" value="latest">Latest suggestion</SelectItem>
        {turns.slice(0, -1).map((turn, i) => <SelectItem key={turn.id} value={turn.id} data-testid={`kb-ai-history-${i}`}>Suggestion {i + 1}</SelectItem>)}
      </SelectContent></Select></div>}
      {suggestion && <section data-testid="kb-ai-suggestion" className="border-t pt-4">
        <p data-testid="kb-ai-preview-state" className="mb-3 text-xs font-medium text-slate-500">{busy ? 'Writing suggestion…' : complete ? 'Suggestion preview' : 'Incomplete suggestion'}</p>
        <div className="kb-content text-sm text-slate-700 [overflow-wrap:anywhere]" dangerouslySetInnerHTML={{ __html: suggestion }} />
        {complete && sources.length > 0 && (
          <div className="mt-3 rounded-lg bg-slate-50 p-2.5" data-testid="kb-ai-sources-used">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Sources this draft used</p>
            <ul className="mt-1 space-y-0.5">
              {sources.map((item, index) => (
                <li key={`${item.label}-${index}`} className="text-[11px] text-slate-600 truncate">
                  <span className="text-slate-400">{item.kind}</span> · {item.label} {item.detail ? `· ${item.detail}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
        {complete && sources.length === 0 && (
          <p className="mt-3 text-[11px] text-amber-700" data-testid="kb-ai-no-sources">
            No internal sources were used - treat every fact in this draft as unconfirmed.
          </p>
        )}
      </section>}
    </div>
    <footer className="shrink-0 border-t p-3 flex flex-wrap gap-2 bg-slate-50">
      <Button data-testid="kb-ai-replace" size="sm" disabled={!complete || busy} onClick={() => onApply(suggestion, 'replace')}><Check className="mr-1 h-4 w-4" />Replace content</Button>
      <Button data-testid="kb-ai-append" size="sm" variant="outline" disabled={!complete || busy} onClick={() => onApply(suggestion, 'append')}><Plus className="mr-1 h-4 w-4" />Append</Button>
      <Button data-testid="kb-ai-undo" size="icon" variant="ghost" title="Undo last AI edit" disabled={!canUndo || busy} onClick={onUndo}><RotateCcw className="h-4 w-4" /></Button>
    </footer>
    </>)}
  </aside>;
};